import * as Sentry from '@sentry/react'

const STORAGE_KEY = 'cat_sse_reconnect_pending'
const RETRY_DELAY_MS = 3 * 60 * 1000
const FLUSH_TIMEOUT_MS = 2000
const METRIC_ATTRIBUTES = {
  feature: 'sse',
  event_type: 'reconnecting',
}

type PendingReconnect = {
  startTimestamp: string
  startEpochMs: number
  endTimestamp?: string
  endEpochMs?: number
  startMetricFlushed?: boolean
  streamIds: string[]
}

let retryTimer: number | null = null

function hasSentryDsn() {
  return Boolean(import.meta.env.VITE_SENTRY_DSN)
}

function readPending(): PendingReconnect | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as PendingReconnect
  } catch {
    return null
  }
}

function writePending(pending: PendingReconnect) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(pending))
  } catch {
    // Ignore storage failures; Sentry metrics below can still help.
  }
}

function clearPending() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures.
  }
}

function scheduleRetry() {
  if (retryTimer !== null) return
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    void flushPendingReconnectMetrics()
  }, RETRY_DELAY_MS)
}

async function flushSentryMetrics() {
  const sent = await Sentry.flush(FLUSH_TIMEOUT_MS)
  if (!sent) {
    scheduleRetry()
  }
  return sent
}

function recordReconnectStartMetrics(pending: PendingReconnect) {
  Sentry.metrics.count('sse_reconnect_started_count', 1, {
    attributes: METRIC_ATTRIBUTES,
  })

  Sentry.metrics.gauge('sse_reconnect_active', 1, {
    attributes: METRIC_ATTRIBUTES,
  })

  Sentry.metrics.gauge('sse_reconnect_timestamp_ms', pending.startEpochMs, {
    attributes: {
      ...METRIC_ATTRIBUTES,
      phase: 'start',
    },
  })
}

function recordReconnectEndMetrics(pending: PendingReconnect) {
  if (!pending.endEpochMs) return

  const durationMs = pending.endEpochMs - pending.startEpochMs

  Sentry.metrics.count('sse_reconnect_ended_count', 1, {
    attributes: METRIC_ATTRIBUTES,
  })

  Sentry.metrics.gauge('sse_reconnect_active', 0, {
    attributes: METRIC_ATTRIBUTES,
  })

  Sentry.metrics.gauge('sse_reconnect_timestamp_ms', pending.endEpochMs, {
    attributes: {
      ...METRIC_ATTRIBUTES,
      phase: 'end',
    },
  })

  Sentry.metrics.distribution('sse_reconnect_duration_ms', durationMs, {
    attributes: METRIC_ATTRIBUTES,
    unit: 'millisecond',
  })

  Sentry.metrics.distribution(
    'sse_reconnect_sentry_registration_delay_ms',
    Date.now() - pending.startEpochMs,
    {
      attributes: {
        ...METRIC_ATTRIBUTES,
        browser_online: String(navigator.onLine),
      },
      unit: 'millisecond',
    }
  )
}

async function flushPendingReconnectMetrics() {
  const pending = readPending()
  if (!pending) return

  if (!hasSentryDsn()) {
    clearPending()
    return
  }

  if (!pending.startMetricFlushed) {
    recordReconnectStartMetrics(pending)
  }
  if (pending.endEpochMs) {
    recordReconnectEndMetrics(pending)
  }

  const sent = await flushSentryMetrics()
  if (!sent) return

  if (pending.endEpochMs) {
    clearPending()
    return
  }

  writePending({ ...pending, startMetricFlushed: true })
}

export function recordSseReconnectStarted(streamIds: string[]) {
  if (typeof window === 'undefined') return

  const existing = readPending()
  if (existing) return

  const now = Date.now()
  const pending = {
    startTimestamp: new Date(now).toISOString(),
    startEpochMs: now,
    streamIds,
  }

  writePending(pending)

  if (!hasSentryDsn()) {
    clearPending()
    return
  }

  recordReconnectStartMetrics(pending)
  void flushSentryMetrics().then(sent => {
    if (!sent) return

    const latest = readPending()
    if (latest?.startEpochMs === pending.startEpochMs) {
      writePending({ ...latest, startMetricFlushed: true })
    }
  })
}

export function recordSseReconnectEnded() {
  if (typeof window === 'undefined') return

  const pending = readPending()
  if (!pending) return
  if (pending.endEpochMs) return

  const endEpochMs = Date.now()
  const completedPending = {
    ...pending,
    endTimestamp: new Date(endEpochMs).toISOString(),
    endEpochMs,
  }
  writePending(completedPending)

  if (!hasSentryDsn()) {
    clearPending()
    return
  }

  void flushPendingReconnectMetrics()
}

export function resumePendingSseReconnectTelemetry() {
  if (typeof window === 'undefined') return
  if (readPending()) {
    void flushPendingReconnectMetrics()
  }
}
